import { isHiddenB2CPath } from "@/config/featureFlags";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Search, Menu, X, UserCircle, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "@/lib/router-compat";
import { useAuth } from "@/hooks/useAuth";
import { useLifeMart } from "@/hooks/useLifeMart";
import { OPEN_BETA_MODE } from "@/config/featureFlags";
import { supabase } from "@/integrations/supabase/client";
import logo from "@/assets/logo-new-final.png";
import lifemartLogo from "@/assets/lp/lifemart-logo.svg";
import NotificationCenter from "@/components/NotificationCenter";
import { featuredArticles, popularArticles, mustReadArticles, latestPosts } from "@/data/articles";
import { guides } from "@/data/guides";
import { experts } from "@/data/experts";
import GoalMenuPanel from "@/components/header/GoalMenuPanel";
import WalletChips from "@/components/wallet/WalletChips";
import UserAccountMenu from "@/components/UserAccountMenu";
import { goals, type Goal } from "@/components/header/goalsConfig";

// Flatten goal links for the search index (replaces old toolsLinks/educationLinks)
const goalSearchLinks = goals.flatMap((g) =>
  g.links.map((l) => ({ label: l.label, href: l.href, category: g.shortLabel }))
);

type NavCategory =
  | { label: string; goal: Goal; href?: undefined }
  | { label: string; href: string; goal?: undefined };

const navCategories: NavCategory[] = [
  ...goals.map<NavCategory>((g) => ({ label: g.question, goal: g })),
  { label: "Guides", href: "/guides" },
  { label: "Blog", href: "/blog" },
  { label: "Stack", href: "/stack" },
  { label: "Meet Homie", href: "/homie" },
];

/** Single nav button: shows balance + links to /rewards when earned, otherwise Dashboard → /dashboard */
const Header = ({ minimal = false }: { minimal?: boolean }) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const { user } = useAuth();
  const { isLifeMartMember } = useLifeMart();
  const searchRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();

  const handleNavClick = (href: string) => {
    setMobileMenuOpen(false);
    setOpenDropdown(null);
    if (href.startsWith("/#")) {
      const id = href.slice(2);
      if (window.location.pathname === "/") {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate("/");
        setTimeout(() => {
          document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
        }, 300);
      }
    }
  };

  // Fetch live blog posts for search
  const [liveBlogPosts, setLiveBlogPosts] = useState<{ title: string; category: string; url: string; image: string; type: "article" }[]>([]);
  
  useEffect(() => {
    const fetchBlogPosts = async () => {
      const { data } = await supabase
        .from("blog_posts")
        .select("slug, title, category, cover_image_url")
        .eq("status", "published")
        .order("published_at", { ascending: false });
      if (data) {
        setLiveBlogPosts(data.map(p => ({
          title: p.title,
          category: p.category,
          url: p.title.toLowerCase().includes("guide") ? `/guides/${p.slug}` : `/blog/${p.slug}`,
          image: p.cover_image_url || "",
          type: "article" as const,
        })));
      }
    };
    fetchBlogPosts();
  }, []);

  // Combine all searchable items: live blog posts + static articles + tools + education links
  const allSearchableItems = useMemo(() => {
    const staticArticles = [...featuredArticles, ...popularArticles, ...mustReadArticles, ...latestPosts].map(a => ({
      title: a.title,
      category: a.category,
      url: a.url,
      image: a.image,
      type: "article" as const,
    }));
    // Deduplicate by title (live posts take priority)
    const liveTitles = new Set(liveBlogPosts.map(p => p.title.toLowerCase()));
    const dedupedStatic = staticArticles.filter(a => !liveTitles.has(a.title.toLowerCase()));
    const guideItems = guides.map(g => ({
      title: g.title,
      category: "Guide",
      url: `/guides/${g.slug}`,
      image: g.heroImage || "",
      type: "article" as const,
    }));
    const expertItems = experts.map(e => ({
      title: e.name,
      category: e.role,
      url: `/experts/${e.id}`,
      image: e.avatar || "",
      type: "page" as const,
    }));
    const pages = [
      ...goalSearchLinks.map(l => ({ title: l.label, category: l.category, url: l.href, image: "", type: "page" as const })),
      { title: "Find an Expert", category: "Experts", url: "/experts", image: "", type: "page" as const },
      { title: "Stack", category: "Tools", url: "/stack", image: "", type: "page" as const },
      { title: "Leaderboard", category: "Community", url: "/leaderboard", image: "", type: "page" as const },
      { title: "Homebuying Support", category: "Support", url: "/homebuying-support", image: "", type: "page" as const },
      { title: "Success Stories", category: "Community", url: "/success-stories", image: "", type: "page" as const },
      { title: "Relocation Calculator", category: "Tools", url: "/relocation-calculator", image: "", type: "page" as const },
      { title: "Neighborhood Watchlist", category: "Tools", url: "/neighborhood-watchlist", image: "", type: "page" as const },
      { title: "LifeMart Deals", category: "Benefits", url: "/lifemart", image: "", type: "page" as const },
      { title: "Install App", category: "General", url: "/install", image: "", type: "page" as const },
      { title: "Points Store", category: "Wallet", url: "/points-store", image: "", type: "page" as const },
    ];
    return [...liveBlogPosts, ...dedupedStatic, ...guideItems, ...pages].filter((i) => !isHiddenB2CPath(i.url.split(/[?#]/)[0]));
  }, [liveBlogPosts]);

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return allSearchableItems.filter(
      (a) =>
        a.title.toLowerCase().includes(q) ||
        a.category.toLowerCase().includes(q)
    ).slice(0, 8);
  }, [searchQuery, allSearchableItems]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
        setSearchQuery("");
      }
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-card/95 backdrop-blur-md">
      <div className="container mx-auto flex items-center justify-between gap-4 px-4 py-2 xl:py-3 xl:px-8 min-h-[88px] sm:min-h-[104px] xl:min-h-[120px]">
        {/* Mobile menu toggle */}
        {!minimal && (
          <button
            className="xl:hidden p-2 text-foreground hover:text-brand transition-colors flex-shrink-0"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        )}

        {/* Logo – flows in flex so justify-between naturally centers it on mobile */}
        <Link to="/" className="flex items-center gap-2 flex-shrink-0">
          <img data-site-logo src={logo} alt="Home Approach" width={1904} height={1920} className="h-[68px] sm:h-[88px] xl:h-[104px] w-auto" />
        </Link>

        {/* Desktop nav */}
        {!minimal && (
          <nav className="hidden xl:flex items-center gap-4 2xl:gap-6" aria-label="Main navigation" ref={navRef}>
            {navCategories.map((cat) => {
              if (cat.goal) {
                const isOpen = openDropdown === cat.label;
                return (
                  <div key={cat.label} className="relative">
                    <button
                      onClick={() => setOpenDropdown(isOpen ? null : cat.label)}
                      className="flex items-center gap-1 text-xs xl:text-sm font-semibold text-primary hover:text-primary/80 transition-colors whitespace-nowrap"
                    >
                      {cat.label}
                      <ChevronDown size={14} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence>
                      {isOpen && (
                        <GoalMenuPanel
                          goal={cat.goal}
                          onNavigate={() => setOpenDropdown(null)}
                        />
                      )}
                    </AnimatePresence>
                  </div>
                );
              }

              const cls = "text-xs xl:text-sm font-semibold text-primary hover:text-primary/80 transition-colors whitespace-nowrap";

              return (
                <Link key={cat.label} to={cat.href!} className={cls}>
                  {cat.label}
                </Link>
              );
            })}
          </nav>
        )}

        {/* Search + Auth */}
        <div className="flex items-center gap-2" ref={searchRef}>
          {user && <NotificationCenter />}
          {user && <WalletChips />}
          {/* Ollama-compliance debug dot lived here (dev-only) — pulled it out.
              That's local dev-machine tooling state, not product data; we report
              on it directly from our own environment instead of shipping it in
              the app's source tree. */}
          {!user && (
            <Link
              to="/request-access"
              className="hidden sm:inline-flex items-center rounded-full bg-accent px-3 py-1.5 text-xs font-semibold text-accent-foreground hover:bg-accent/90 transition-colors"
            >
              Request access
            </Link>
          )}
          <Link
            to={user ? "/dashboard" : "/auth"}
            aria-label={user ? "Dashboard" : "Sign In"}
            className="flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            <UserCircle size={16} />
            <span className="hidden sm:inline">{user ? "Dashboard" : "Sign In"}</span>
          </Link>
          {user && <UserAccountMenu signOutTo="/" />}

          <div className="relative">
            <AnimatePresence>
              {searchOpen && (
                <motion.input
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: 200, opacity: 1 }}
                  exit={{ width: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  type="text"
                  placeholder="Search site..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                  className="rounded-full border border-border bg-muted px-4 py-1.5 text-sm text-foreground outline-hidden focus:ring-2 focus:ring-brand"
                />
              )}
            </AnimatePresence>
            {searchOpen && searchQuery.trim() && (
              <div className="absolute right-0 top-10 w-80 max-h-96 overflow-y-auto rounded-xl border border-border bg-card shadow-xl z-50">
                {searchResults.length > 0 ? (
                  searchResults.map((item, idx) => {
                    const isExternal = item.url.startsWith("http");
                    const El = isExternal ? "a" : Link;
                    const linkProps = isExternal ? { href: item.url } : { to: item.url };
                    return (
                      <El
                        key={`${item.title}-${idx}`}
                        {...(linkProps as any)}
                        onClick={() => { setSearchOpen(false); setSearchQuery(""); }}
                        className="flex items-start gap-3 p-3 hover:bg-muted transition-colors border-b border-border last:border-b-0"
                      >
                        {item.image ? (
                          <img src={item.image} alt={item.title} className="h-12 w-16 rounded-md object-cover flex-shrink-0" />
                        ) : (
                          <div className="h-12 w-16 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                            <Search size={14} className="text-muted-foreground" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground line-clamp-2">{item.title}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{item.category}</p>
                        </div>
                      </El>
                    );
                  })
                ) : (
                  <p className="p-4 text-sm text-muted-foreground text-center">No results found</p>
                )}
              </div>
            )}
          </div>
          <button
            onClick={() => {
              setSearchOpen(!searchOpen);
              if (searchOpen) setSearchQuery("");
            }}
            className="p-2 text-muted-foreground hover:text-brand transition-colors"
            aria-label="Search"
          >
            <Search size={20} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {!minimal && (
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.nav
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="xl:hidden overflow-hidden border-t border-border bg-card"
            >
              <div className="px-4 py-4 space-y-1" role="navigation" aria-label="Mobile navigation">
                {navCategories.map((cat) => {
                  if (cat.goal) {
                    const isExpanded = mobileExpanded === cat.label;
                    return (
                      <div key={cat.label}>
                        <button
                          onClick={() => setMobileExpanded(isExpanded ? null : cat.label)}
                          className="flex w-full items-center justify-between py-2.5 text-sm font-semibold text-primary"
                        >
                          {cat.label}
                          <ChevronDown size={16} className={`transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                        </button>
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden pl-2 pb-2 space-y-1"
                            >
                              <Link
                                to={cat.goal.primaryHref}
                                onClick={() => setMobileMenuOpen(false)}
                                className="block mb-2 mx-1 px-3 py-2 text-xs font-semibold text-primary-foreground bg-primary rounded-lg text-center"
                              >
                                {cat.goal.primaryLabel} →
                              </Link>
                              {cat.goal.links.map((entry) => {
                                const Icon = entry.icon;
                                return (
                                  <Link
                                    key={entry.label}
                                    to={entry.href}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="flex items-start gap-3 py-2 px-2 -mx-2 rounded-lg hover:bg-muted transition-colors"
                                  >
                                    <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-brand/10 text-brand mt-0.5">
                                      <Icon size={15} />
                                    </span>
                                    <div className="min-w-0 flex-1">
                                      <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="text-sm font-semibold text-foreground leading-tight">
                                          {entry.label}
                                        </span>
                                      </div>
                                      <p className="text-xs text-muted-foreground mt-0.5 leading-snug line-clamp-1">
                                        {entry.description}
                                      </p>
                                    </div>
                                  </Link>
                                );
                              })}
                              {!user && (
                                <Link
                                  to="/auth"
                                  onClick={() => setMobileMenuOpen(false)}
                                  className="block mt-2 mx-1 px-3 py-2 text-xs font-semibold text-brand bg-brand/10 rounded-lg text-center"
                                >
                                  Members earn rewards → Create free account
                                </Link>
                              )}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  }

                  const cls = "block py-2.5 text-sm font-semibold text-primary hover:text-primary/80 transition-colors";

                  return (
                    <Link
                      key={cat.label}
                      to={cat.href!}
                      onClick={() => setMobileMenuOpen(false)}
                      className={cls}
                    >
                      {cat.label}
                    </Link>
                  );
                })}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      )}
    </header>
  );
};

export default Header;
